import { useCallback, useEffect, useState } from "react";
import { Box, Divider, Typography } from "@mui/material";
import { InputField } from "../../components/ui/Inputs";
import { OutlinedSmallButton, PrimaryButton } from "../../components/ui/Button";
import { ArrowForward } from "@mui/icons-material";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import service from "../../services/api/service";
import { useDispatch, useSelector } from "react-redux";
import { setUserId } from "../../services/redux/slices/unauthSlice";
import Toast from "../../components/ui/Toast";
import InputLabelComp from "../../components/ui/InputLabelComp";
import ButtonLoader from "../../components/ui/ButtonLoader";
import { ROUTES } from "../../routes/routerConfig";
import {
  authSelector,
  clearAuth,
  setOtpUserData,
  setTriggerData,
} from "../../services/redux/slices/authSlice";
import { encrypt, decrypt } from "../../utils/crypto";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice";
import ReactGA from "react-ga4";
import { trackEvent } from "../../utils/ga";
import { resetKeys } from "../../services/redux/slices/keyRegistrarSlice";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import LoaderBackdrop from "../../components/ui/LoaderBackdrop";
import { useTriggerData } from "../../hooks/useTriggerData";
import { setTrackingId } from "../../services/redux/slices/stepperDynamicFormSlice";
import { persistor } from "../../services/redux/store";

type Inputs = {
  cif_mobile_email: string;
};

type props = {
  setNextStep: (step: string) => void;
};

const Login = ({ setNextStep }: props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [apiResponseData, setApiResponseData] = useState();
  const { authStatus } = useSelector(authSelector);
  const [toastOpen, setToastOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<
    "error" | "warning" | "info" | "success"
  >("success");
  const [isCoApplicantOtpLoading, setIsCoApplicantOtpLoading] = useState(false);
  const { control, handleSubmit, watch } = useForm<Inputs>({
    defaultValues: {
      cif_mobile_email: "",
    },
  });
  const handleToastClose = () => setToastOpen(false);
  const { isTriggerDataEmpty, triggerData }: any = useTriggerData();
  const [selected, setSelected] = useState("login");

  // // On Login Page Load
  // useEffect(() => {
  //   // if(!localStorage.getItem("token")) {
  //     persistor.purge();
  //   // }
  // }, [])

  // Validate URL parameters on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token: any = params.get("token");
    const source = params.get("source");

    if (token) {
      const decryptedToken = decrypt(token);
      const triggerData = JSON.parse(decryptedToken);
      dispatch(setTriggerData(triggerData));
      dispatch(setTrackingId(triggerData?.submissionTrackingId));
    }
  }, [location.search, navigate]);


  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    newSelection: string
  ) => {
    if (newSelection !== null) {
      setSelected(newSelection);
      setNextStep(ROUTES?.Login?.register?.path);
    }
  };

  const handleToastOpen = (
    message: string,
    severity: "error" | "warning" | "info" | "success"
  ) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const onSignUp = () => {
    setNextStep(ROUTES?.Login?.register?.path);
  };


  const handleSendCoApplicantOtp = (data: any) => {
    if (String(data?.cif_mobile_email) != String(triggerData?.coApplicantMobileNumber)) {
      dispatch(setStatusPopup({
        status: "error",
        message: "You are not authorized to access this page",
      }));
      return;
    } else {
      setIsCoApplicantOtpLoading(true);
      service?.auth?.sendCoApplicantOtp({
        key: encrypt(triggerData?.coApplicantMobileNumber),
        trackingId: triggerData?.submissionTrackingId,
      }).then((res) => {
        setIsCoApplicantOtpLoading(false);
        setNextStep(ROUTES?.Login?.otp?.path);
      }).catch((error) => {
        dispatch(
          setStatusPopup({
            status: "error",
            message: error.response?.data?.message || "An error occurred",
          })
        );
        setIsCoApplicantOtpLoading(false);
      });
    }
  };


  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    trackEvent("Enter_CIF_Continue", "", {
      label: "Enter_CIF_Continue",
      category: "Enter_CIF_Continue",
      action: "Enter_CIF_Continue",
    });
    setIsLoading(true);
    let value = typeof data.cif_mobile_email === "string"
      ? data.cif_mobile_email.toLowerCase()
      : data.cif_mobile_email
    localStorage.setItem("mobile", value);
    const payload = { key: encrypt(value) };
    service?.auth
      ?.sendUserOtp(payload)
      .then((res) => {
        dispatch(resetKeys());
        handleToastOpen(res?.data?.message, "success");
        dispatch(setOtpUserData(res?.data?.data));
        setApiResponseData(res?.data?.data);
        // navigate(ROUTES?.Login?.otp?.locationPath);
        setNextStep(ROUTES?.Login?.otp?.path);
        setIsLoading(false);
      })
      .catch((error) => {
        const errorMessage =
          error.response?.data?.message || "An error occurred";
        dispatch(
          setStatusPopup({
            status: "error",
            // message: `Disbursement requests are not permitted for this loan.`,
            message: errorMessage,
          })
        );
        setIsLoading(false);
      });
  };

  const buttonDisable: boolean = !!watch("cif_mobile_email").length;
  // useEffect(() => {
  //   if (authStatus) {
  //     return;
  //   }
  //   dispatch(clearAuth());
  // }, []);

  const onKeyDownHandler = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      // Prefer 'e.key' over 'e.keyCode'
      e.preventDefault(); // Prevent default form submission behavior
      handleSubmit(
        isTriggerDataEmpty ?
          onSubmit :
          triggerData?.toUser ? onSubmit : handleSendCoApplicantOtp
      )(); // Properly invoke handleSubmit with the onSubmit handler
    }
  };

  const showLoginInputLabel = useCallback(() => {
    if (isTriggerDataEmpty) {
      return "CIF/Email/Mobile"
    } else {
      if (triggerData?.toUser) {
        return "CIF/Email/Mobile"
      } else {
        return "Mobile"
      }
    }
  }, [isTriggerDataEmpty, triggerData]);


  return (
    <>
      <LoaderBackdrop open={isCoApplicantOtpLoading} />
      <Box>
        <Toast
          open={toastOpen}
          message={toastMessage}
          severity={toastSeverity}
          onClose={handleToastClose}
        />

        <Box className="main-wrapper">
          <Box className="head-section" sx={{ width: ["100%", "-webkit-fill-available"] }}>
            <Box className="heading">Let’s Start!👋</Box>
            {/* <Box className="heading">Registered User</Box> */}

            {isTriggerDataEmpty && (
              <>
                <ToggleButtonGroup
                  value={selected}
                  exclusive
                  onChange={handleChange}
                  sx={{
                    height: "40px",
                    backgroundColor: "#eaf3f5", // container bg
                    borderRadius: "7px",
                    padding: "4px",
                    display: "inline-flex",
                    border: "none", // remove group border
                    width: "100%",
                    gap: "6px",
                    marginTop: "10px"
                  }}
                >
                  <ToggleButton
                    value="login"
                    sx={{
                      flex: 1,
                      borderRadius: "6px !important", // fully rounded
                      textTransform: "none",
                      border: "none", // remove inner borders
                      "&.Mui-selected": {
                        backgroundColor: "#ffffff",
                        color: "#0e8a9e",
                        // boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
                      },
                      "&:hover": {
                        backgroundColor: "#ffffff",
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: "#ffffff", // Ensures no hover effect when selected
                      },
                      "&.Mui-focusVisible": {
                        backgroundColor: "#ffffff",
                      },
                    }}
                  >
                    Login
                  </ToggleButton>
                  <ToggleButton
                    value="register"
                    sx={{
                      flex: 1,
                      borderRadius: "6px !important", // fully rounded
                      textTransform: "none",
                      border: "none", // remove inner borders
                      "&.Mui-selected": {
                        backgroundColor: "#ffffff",
                        color: "#0e8a9e",
                        // boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
                      },
                      "&:hover": {
                        backgroundColor: "#ffffff",
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: "#ffffff", // Ensures no hover effect when selected
                      },
                      "&.Mui-focusVisible": {
                        backgroundColor: "#ffffff",
                      },

                    }}
                  >
                    Register
                  </ToggleButton>
                </ToggleButtonGroup>
              </>
            )}

            <Box className="sub-heading" sx={{ paddingTop: "10px" }}>
              Please enter your details to access your loan account.
            </Box>
          </Box>

          {/* <form> */}
          <Controller
            name="cif_mobile_email"
            control={control}
            render={({ field }) => (
              <>
                <InputLabelComp title={`${showLoginInputLabel()}`} css={true} />
                <InputField
                  {...field}
                  // autoFocus
                  fullWidth
                  borderRadius="6px"
                  placeholder={`Enter ${showLoginInputLabel()}`}
                  autoComplete="off"
                  onKeyDown={(e: any) => onKeyDownHandler(e)}
                />
              </>
            )}
          />

          <PrimaryButton
            type="button"
            fullWidth
            //endIcon={<ArrowForward color="inherit" fontSize="small" />}
            onClick={handleSubmit(isTriggerDataEmpty ? onSubmit : triggerData?.toUser ? onSubmit : handleSendCoApplicantOtp)}
            disabled={!buttonDisable || isLoading}
          >
            <ButtonLoader title="Continue" isLoading={isLoading} />
          </PrimaryButton>

          <span className="term-condtion">
            By clicking on <b>‘Continue’</b>, you accept that you have read our
            <Link
              to={"https://www.avanse.com/terms-conditions"}
              target="_blank"
              style={{ marginRight: "5px" }}
            >
              <b> Terms & Conditions</b>
            </Link>
            and
            <Link to={"https://www.avanse.com/privacy-policy"} target="_blank">
              <b> Privacy Policy</b>
            </Link>

            {/* <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
            my={2} // Add some vertical margin
          >
            <Divider
              sx={{
                flexGrow: 1,
                height: "1px",
                background: "rgba(0, 0, 0, 0.12)",
              }}
            />
            <Typography
              variant="body2"
              sx={{ mx: 2, color: "rgba(0, 0, 0, 0.54)" }}
            >
              Or
            </Typography>
            <Divider
              sx={{
                flexGrow: 1,
                height: "1px",
                background: "rgba(0, 0, 0, 0.12)",
              }}
            />
          </Box> */}
            {/* <Box className="heading">Register Now </Box>
          <p className="reg-text-con" onClick={() => onSignUp()}>
            <span className="span1">Not Registered Yet?</span>
            <span className="span2">Register Now</span>
          </p> */}
            {/* <Box className="heading">Register Now </Box>
          <p>
            If you are not registered yet, please {""}
            <span onClick={() => onSignUp()}>
              <b style={{ cursor: "pointer", textDecoration: "underline" }}>
                sign up
              </b>{" "}
              now
            </span>
          </p> */}
          </span>
          {/* </form> */}
        </Box>
      </Box>
    </>
  );
};

export default Login;